import { api, download } from './client'
import type {
  AbmApiStatus,
  AbmStatus,
  AdeProfile,
  OrgActivationLock,
  ActivationResult,
  ApplicationStatus,
  BalanceTransaction,
  CommandCancelResult,
  CreateDistributorOrganization,
  CreatedDistributorOrganization,
  DashboardStats,
  Distributor,
  DistributorStatus,
  DistributorTransaction,
  DistributorTransactionType,
  Employee,
  MembershipRole,
  Notification,
  OrganizationSettings,
  OrganizationStatus,
  PartnerApplication,
  TopUpDiscount,
  UpdateDistributorOrganization,
  UpdateOrganization,
  UpdateOrganizationSettings,
  AppleStatus,
  Contract,
  ContractFilters,
  CreateContract,
  CreatePayment,
  Customer,
  CustomerFilters,
  DeviceDebt,
  DeviceRental,
  ImportDevice,
  ImportResult,
  Payment,
  PaymentFilters,
  PaymentMethod,
  PaymentSummary,
  Policy,
  RenewContract,
  SaveCustomer,
  SyncResult,
  UpdateContract,
  DeviceActionResult,
  AppStoreApp,
  DeviceApps,
  DeviceCommand,
  DeviceCommandRequest,
  DeviceDetail,
  DeviceEvent,
  DeviceFilters,
  DeviceListItem,
  DeviceLocation,
  MapMarker,
  EnrollmentLinks,
  Me,
  PaginatedRes,
  Paging,
} from './types'

/**
 * Every mdm-service endpoint the console calls, one object per resource. Paths are
 * relative to `VITE_API_BASE_URL` (`/api/v1`).
 */

export const me = {
  /** Also refreshes the user's copy on the server — call once per sign-in. */
  get: () => api.get<Me>('/me'),
}

/** The platform's Apple credentials (platform admins only). */
export const platformApple = {
  status: () => api.get<AppleStatus>('/platform/apple'),
  generateCsr: () => api.post<AppleStatus>('/platform/apple/apns/csr'),
  downloadCsr: () => download('/platform/apple/apns/csr'),
  /** Fields: certificate, privateKey?, passphrase?, allowTopicChange?, secondary? */
  uploadApns: (form: FormData) => api.upload<AppleStatus>('/platform/apple/apns', form),
  removeSecondaryApns: (id: string) => api.delete<AppleStatus>(`/platform/apple/apns/secondary/${id}`),
  generateCa: () => api.post<AppleStatus>('/platform/apple/ca/generate'),
  /** Fields: certificate, privateKey?, passphrase?, replace? */
  importCa: (form: FormData) => api.upload<AppleStatus>('/platform/apple/ca', form),
  downloadCa: () => download('/platform/apple/ca/certificate'),
}

export const devices = {
  search: (filters: DeviceFilters, paging: Paging) =>
    api.get<PaginatedRes<DeviceListItem>>('/devices', { ...filters, ...paging }),
  get: (id: string) => api.get<DeviceDetail>(`/devices/${id}`),
  rename: (id: string, customName: string | null) => api.put<void>(`/devices/${id}`, { customName }),
  remove: (id: string) => api.delete<void>(`/devices/${id}`),
  commands: (id: string) => api.get<DeviceCommand[]>(`/devices/${id}/commands`),
  events: (id: string) => api.get<DeviceEvent[]>(`/devices/${id}/events`),
  locations: (id: string) => api.get<DeviceLocation[]>(`/devices/${id}/locations`),
  /** The phone's apps from its last InstalledApplicationList answers. */
  apps: (id: string) => api.get<DeviceApps>(`/devices/${id}/apps`),
  command: (id: string, body: DeviceCommandRequest) => api.post<DeviceActionResult>(`/devices/${id}/commands`, body),
  /** Only while the phone has not fetched it (PENDING); 409 once it has. */
  cancelCommand: (id: string, commandId: string) => api.post<CommandCancelResult>(`/devices/${id}/commands/${commandId}/cancel`),
  lock: (id: string, body: { phoneNumber?: string; message?: string }) =>
    api.post<DeviceActionResult>(`/devices/${id}/lock`, body),
  unlock: (id: string) => api.post<DeviceActionResult>(`/devices/${id}/unlock`),
  unenroll: (id: string) => api.post<DeviceActionResult>(`/devices/${id}/unenroll`),
  /** The phone's wallpaper: field "file" (PNG or JPEG) and "where" (LOCK, HOME or BOTH). */
  wallpaper: (id: string, form: FormData) => api.upload<DeviceActionResult>(`/devices/${id}/wallpaper`, form),
  /** Platform staff: set or generate the device PIN. */
  pin: (id: string, pin?: string) => api.post<{ pin: string }>(`/devices/${id}/pin`, pin ? { pin } : {}),
  /** Platform staff: the stored Activation Lock bypass code. */
  bypassCode: (id: string) => api.get<DeviceActionResult>(`/devices/${id}/bypass-code`),
  /** Platform admins: move to another organization. */
  transfer: (id: string, organizationId: string) => api.post<void>(`/devices/${id}/transfer`, { organizationId }),
  /** The renter card: renter, contract and the rental policy's verdict. */
  rental: (id: string) => api.get<DeviceRental>(`/devices/${id}/rental`),
  /** Set or clear the debt and the date it locks the device. */
  debt: (id: string, body: DeviceDebt) => api.put<Policy>(`/devices/${id}/debt`, body),
  applyPolicy: (id: string) => api.post<Policy>(`/devices/${id}/policy`),
  /** Activation Lock to the organization through ABM (admins, operators, platform staff). */
  orgActivationLock: (id: string) => api.get<OrgActivationLock>(`/devices/${id}/org-activation-lock`),
  orgActivationLockAction: (id: string, action: 'lock' | 'unlock', lostMessage?: string) =>
    api.post<OrgActivationLock>(`/devices/${id}/org-activation-lock`, { action, lostMessage }),
  /** One-time activation from the organization's balance (402 when short). */
  activate: (id: string) => api.post<ActivationResult>(`/devices/${id}/activate`),
  /** Register devices ahead of enrollment. */
  import: (devices: ImportDevice[]) => api.post<ImportResult>('/devices/import', { devices }),
}

export const appStore = {
  /** iPhone apps by name, App Store link or id (Uzbekistan's store); empty when Apple does not answer. */
  search: (q: string) => api.get<AppStoreApp[]>('/app-store/search', { q }),
}

export const customers = {
  search: (filters: CustomerFilters, paging: Paging) =>
    api.get<PaginatedRes<Customer>>('/customers', { ...filters, ...paging }),
  get: (id: string) => api.get<Customer>(`/customers/${id}`),
  create: (body: SaveCustomer) => api.post<Customer>('/customers', body),
  update: (id: string, body: SaveCustomer) => api.put<Customer>(`/customers/${id}`, body),
  remove: (id: string) => api.delete<void>(`/customers/${id}`),
}

export const contracts = {
  search: (filters: ContractFilters, paging: Paging) =>
    api.get<PaginatedRes<Contract>>('/contracts', { ...filters, ...paging }),
  get: (id: string) => api.get<Contract>(`/contracts/${id}`),
  create: (body: CreateContract) => api.post<Contract>('/contracts', body),
  update: (id: string, body: UpdateContract) => api.put<Contract>(`/contracts/${id}`, body),
  renew: (id: string, body: RenewContract) => api.post<Contract>(`/contracts/${id}/renew`, body),
  terminate: (id: string) => api.post<Contract>(`/contracts/${id}/terminate`),
}

export const payments = {
  search: (filters: PaymentFilters, paging: Paging) =>
    api.get<PaginatedRes<Payment>>('/payments', { ...filters, ...paging }),
  summary: () => api.get<PaymentSummary>('/payments/summary'),
  create: (body: CreatePayment) => api.post<Payment>('/payments', body),
  pay: (id: string, method?: PaymentMethod) => api.post<Payment>(`/payments/${id}/pay`, method ? { method } : {}),
}

export const rental = {
  /** Run the contract sync for the organization now. */
  sync: () => api.post<SyncResult>('/rental/sync'),
}

export const locations = {
  markers: () => api.get<MapMarker[]>('/locations'),
}

/** Links and QR targets that enroll a phone into the current organization. */
export const enrollment = {
  organizationLinks: () => api.get<EnrollmentLinks>('/enrollment/links'),
  sessionLinks: (customerId?: string) =>
    api.post<EnrollmentLinks>('/enrollment/links', customerId ? { customerId } : {}),
}

/** The caller's organization: settings, balance history, people. */
export const organization = {
  get: () => api.get<OrganizationSettings>('/organization'),
  update: (body: UpdateOrganizationSettings) => api.put<OrganizationSettings>('/organization', body),
  transactions: (paging: Paging) => api.get<PaginatedRes<BalanceTransaction>>('/organization/transactions', { ...paging }),
  employees: () => api.get<Employee[]>('/organization/employees'),
  addEmployee: (body: { fullName: string; username: string; email?: string; role?: MembershipRole }) => api.post<Employee>('/organization/employees', body),
  updateEmployee: (userId: string, body: { fullName: string; email: string; role?: MembershipRole }) =>
    api.put<Employee>(`/organization/employees/${userId}`, body),
  resetPassword: (userId: string) => api.post<Employee>(`/organization/employees/${userId}/password`),
  removeEmployee: (userId: string) => api.delete<void>(`/organization/employees/${userId}`),
}

/** Every organization (platform staff). */
export const platformOrganizations = {
  search: (filters: { search?: string; status?: OrganizationStatus; distributorId?: string; unbound?: boolean }, paging: Paging) =>
    api.get<PaginatedRes<OrganizationSettings>>('/platform/organizations', { ...filters, ...paging }),
  get: (id: string) => api.get<OrganizationSettings>(`/platform/organizations/${id}`),
  create: (body: { name: string; alias?: string; companyName?: string; phone?: string; activationFee?: number }) =>
    api.post<OrganizationSettings>('/platform/organizations', body),
  update: (id: string, body: UpdateOrganization) => api.put<OrganizationSettings>(`/platform/organizations/${id}`, body),
  remove: (id: string) => api.delete<void>(`/platform/organizations/${id}`),
  /** `amount` is what was paid; a discount credits that percent of it on top (500 at 10 % → 550). */
  topUp: (id: string, amount: number, discountPercent: TopUpDiscount, description?: string) =>
    api.post<BalanceTransaction>(`/platform/organizations/${id}/topup`, { amount, discountPercent, description }),
  /** Platform admin only: the balance becomes exactly `balance`. */
  setBalance: (id: string, balance: number, description?: string) =>
    api.put<BalanceTransaction>(`/platform/organizations/${id}/balance`, { balance, description }),
  transactions: (id: string, paging: Paging) =>
    api.get<PaginatedRes<BalanceTransaction>>(`/platform/organizations/${id}/transactions`, { ...paging }),
  pin: (id: string) => api.get<{ pin: string }>(`/platform/organizations/${id}/pin`),
  setPin: (id: string, pin?: string) =>
    api.post<{ pin: string; devicesUpdated: number }>(`/platform/organizations/${id}/pin`, pin ? { pin } : {}),
}

/** Distributors (platform admins). */
export const platformDistributors = {
  search: (filters: { search?: string; status?: DistributorStatus }, paging: Paging) =>
    api.get<PaginatedRes<Distributor>>('/platform/distributors', { ...filters, ...paging }),
  get: (id: string) => api.get<Distributor>(`/platform/distributors/${id}`),
  /** The answer holds the account's temporary password, once. */
  create: (body: { name: string; phone?: string; username: string; email?: string; activationTariff: number; minActivationFee?: number }) =>
    api.post<Distributor>('/platform/distributors', body),
  update: (id: string, body: { name?: string; phone?: string; status?: DistributorStatus; activationTariff?: number; minActivationFee?: number }) =>
    api.put<Distributor>(`/platform/distributors/${id}`, body),
  remove: (id: string) => api.delete<void>(`/platform/distributors/${id}`),
  resetPassword: (id: string) => api.post<Distributor>(`/platform/distributors/${id}/password`),
  /** A payout; refused when the balance does not cover it. */
  withdraw: (id: string, amount: number, description?: string) =>
    api.post<DistributorTransaction>(`/platform/distributors/${id}/withdrawals`, { amount, description }),
  setBalance: (id: string, balance: number, description?: string) =>
    api.put<DistributorTransaction>(`/platform/distributors/${id}/balance`, { balance, description }),
  transactions: (id: string, paging: Paging) =>
    api.get<PaginatedRes<DistributorTransaction>>(`/platform/distributors/${id}/transactions`, { ...paging }),
  bind: (id: string, organizationId: string) => api.put<void>(`/platform/distributors/${id}/organizations/${organizationId}`),
  unbind: (id: string, organizationId: string) => api.delete<void>(`/platform/distributors/${id}/organizations/${organizationId}`),
}

/** The distributor's own cabinet. */
export const distributorCabinet = {
  summary: () => api.get<Distributor>('/distributor'),
  transactions: (filters: { type?: DistributorTransactionType }, paging: Paging) =>
    api.get<PaginatedRes<DistributorTransaction>>('/distributor/transactions', { ...filters, ...paging }),
  organizations: (filters: { search?: string; status?: OrganizationStatus }, paging: Paging) =>
    api.get<PaginatedRes<OrganizationSettings>>('/distributor/organizations', { ...filters, ...paging }),
  organization: (id: string) => api.get<OrganizationSettings>(`/distributor/organizations/${id}`),
  /** The answer holds the new admin's temporary password, once. */
  createOrganization: (body: CreateDistributorOrganization) =>
    api.post<CreatedDistributorOrganization>('/distributor/organizations', body),
  updateOrganization: (id: string, body: UpdateDistributorOrganization) =>
    api.put<OrganizationSettings>(`/distributor/organizations/${id}`, body),
  organizationTransactions: (id: string, paging: Paging) =>
    api.get<PaginatedRes<BalanceTransaction>>(`/distributor/organizations/${id}/transactions`, { ...paging }),
}

/** Partner applications (platform staff). */
export const applications = {
  search: (filters: { search?: string; status?: ApplicationStatus }, paging: Paging) =>
    api.get<PaginatedRes<PartnerApplication>>('/platform/applications', { ...filters, ...paging }),
  approve: (id: string, body: { organizationName?: string; alias?: string; username?: string }) =>
    api.post<PartnerApplication>(`/platform/applications/${id}/approve`, body),
  reject: (id: string, reason?: string) => api.post<PartnerApplication>(`/platform/applications/${id}/reject`, { reason }),
}

export const dashboard = {
  stats: () => api.get<DashboardStats>('/dashboard/stats'),
  notifications: () => api.get<Notification[]>('/notifications'),
}

/** Apple Business Manager: the server token, the ADE profile and the sync (platform staff). */
export const platformAbm = {
  status: () => api.get<AbmStatus>('/platform/abm'),
  /** Field: file (the *_smime.p7m). */
  uploadToken: (form: FormData) => api.upload<AbmStatus>('/platform/abm/token', form),
  sync: () => api.post<AbmStatus>('/platform/abm/sync'),
  profile: () => api.get<AdeProfile>('/platform/abm/ade-profile'),
  updateProfile: (body: AdeProfile) => api.put<AbmStatus>('/platform/abm/ade-profile', body),
}

/** The Apple Business API account: moves Apple Configurator's devices to this server (platform staff). */
export const platformAbmApi = {
  status: () => api.get<AbmApiStatus>('/platform/abm/api'),
  /** Fields: clientId, keyId, file (the .pem private key). */
  connect: (form: FormData) => api.upload<AbmApiStatus>('/platform/abm/api', form),
  disconnect: () => api.delete<AbmApiStatus>('/platform/abm/api'),
  run: () => api.post<AbmApiStatus>('/platform/abm/api/run'),
}
